
// Store
import { createVuexHelpers } from 'vue2-helpers'

// Components
import EventCard from '@/viewsTicketing/Events/components/EventCard.vue'
import Loader from '@/components/Loaders/Loader.vue'
import EventsHeader from './components/EventsHeader.vue'
import LoadMore from '@/components/LoadMore.vue'
import RangePicker from '@/components/RangePicker.vue'
import TicketingInfoModal from '@/components/modals/Ticketing/TicketingInfoModal.vue'
import AppsBanner from '@/components/AppsBanner.vue'

// Icons
import PlaceholderIcon from '@/assets/images/placeholder_admission.svg'

// API
import eventApi from '@/api/events'

// Utils
import * as utils from '@/utils/helpers'
const {
  getMonth,
	getYear,
	getFormattedTime,
	getFormattedDate
}: UtilsTypes = utils
import moment from 'moment'
import { getEventStartDateInfo } from '@/utils/dateUtils'

// Constants
import { ref, onBeforeMount, computed, reactive, defineComponent } from 'vue'
import { IsMobileScreenState, VenueInfoState } from '@/types/storeTypes'
import { DATE_FORMATS, TIME_FORMATS } from '@/constants'
const defaultUnavailabilityData = {
	title: 'Tickets Purchase Unsuccessful',
	description: 'Tickets no longer available'
}

// Router
import { useRouter, type Router, useRoute } from 'vue2-helpers/vue-router'
import { Route } from "vue-router"

// Types
import { MonthsNameShorted, UtilsTypes } from '@/types/utilsTypes'
import { EventEntity, FormattedEventEntity } from '@/types/entitiesTypes'
import { Year } from '@/types/commonTypes'
import { DateRange, EventsDates, EventsPayload, QueryRange } from '@/types/apiTypes'

type RangeFromPicker = DateRange & {
  isDefaultRange: boolean
}

type GroupedEvents = {
	[key: `${MonthsNameShorted} ${Year}`]: FormattedEventEntity[]
}

const defaultRange = null

export default defineComponent({
	name: 'Events',

	components: {
		AppsBanner,
    TicketingInfoModal,
    RangePicker,
		EventCard,
		Loader,
		EventsHeader,
		LoadMore
	},

	setup() {
		let currentPage = 1
		// Store
		const { useState } = createVuexHelpers()
		const { venueInfo }: VenueInfoState = useState('venue', ['venueInfo'])
    const { isMobileScreen }: IsMobileScreenState = useState('ui', [
      'isMobileScreen'
    ])

    // Router
    const router: Router = useRouter()
    const route: Route = useRoute()

		// Reactive
		const state: {
			eventsList: FormattedEventEntity[],
			dateRange: DateRange | null,
			eventsDates: EventsDates,
			unavailabilityData: { title: string, description: string }
		} = reactive({
			eventsList: [],
      eventsDates: [],
      dateRange: defaultRange,
			unavailabilityData: defaultUnavailabilityData
		})

		// Refs
		const loading = ref(false)
		const firstLoading = ref(true)
		const isLastResponseEmpty = ref(false)
		const latestEventStartDate = ref('')
		const isEmptyResult = ref(false)
		const unavailableEventModalOpen = ref(false)
		const isBannerVisible = ref(true)
		const minPickerDate = ref(new Date())

		// Computed
		const notEmptyEvents = computed(() => !!state.eventsList.length)

		const placeholderIcon = computed(() => PlaceholderIcon)

		const eventsGroups = computed(() => Object.keys(events.value))

    const rangePickerFormat = computed<string | boolean>(() => isMobileScreen.value ? 'range': false)

		const isLoadMoreVisible = computed(
			() => notEmptyEvents.value && !loading.value && !isLastResponseEmpty.value
		)

		const events = computed<GroupedEvents>(() => {
			const result: GroupedEvents = {}

			state.eventsList.forEach((event: FormattedEventEntity) => {
        if (!event.monthYear) return

				if (!result[event.monthYear]) {
					result[event.monthYear] = []
				}

					result[event.monthYear].push(event)

			})

			return result
		})

		// Hooks
		onBeforeMount(async () => {
			await getEvents(false, true)

			firstLoading.value = false

      const query = { ...route.query }

      if (query.showUnavailableEventModal) {
				const isTicketsPlural = Number(query.ticketsCount) > 1

				if (Number(query.ticketsCount)) {
					state.unavailabilityData = {
						title: `${isTicketsPlural ? 'Tickets' : 'Ticket'} Purchase Unsuccessful`,
						description: `${isTicketsPlural ? 'Tickets' : 'Ticket'} no longer available`
					}
				} else {
					state.unavailabilityData = defaultUnavailabilityData
				}

        unavailableEventModalOpen.value = true

        delete query.showUnavailableEventModal
        delete query.ticketsCount

        await router.replace({ query })
      }
		})

		// Methods
    const isNextYear = (index: number, key: string): boolean => {
      return index > 1 && key.slice(-1) !== eventsGroups.value[index - 1].slice(-1)
    }

    const closeUnavailableEventModal = () => {
      unavailableEventModalOpen.value = false
    }

    const closeFooter = () => {
			isBannerVisible.value = false
    }

		const handleLoadMore = (): void => {
			currentPage += 1
			getEvents()
		}

    const changeQuery = async (range: RangeFromPicker): Promise<void> => {
      const query = route.query

      if (query.dateFrom === range.dateFrom && query.dateTo === range.dateTo) return

      if (range.isDefaultRange) {
        if (query.dateFrom) {
          await router.replace({ query: {} })
        }
      } else {
        await router.replace({ query: { dateFrom: range.dateFrom, dateTo: range.dateTo} })
      }
    }

    const dateRangeChanged = async (range: RangeFromPicker): Promise<void> => {
      state.dateRange = range.isDefaultRange ? defaultRange : range

      await Promise.all([changeQuery(range), getEvents(true)])
    }

    const getEventsParams = (isDateRangeChanged = false, isFirstRequest = false): EventsPayload => {
      const query = route.query as QueryRange

      if (isDateRangeChanged) {
        currentPage = 1
        isLastResponseEmpty.value = false
      }

      const params: EventsPayload = {
        venueId: venueInfo.value.id,
        page: currentPage,
        status: '2'
      }

      if (isFirstRequest && query?.dateFrom) {
        state.dateRange = {
          dateFrom: query.dateFrom,
          dateTo: query.dateTo
        }
      }

      if (state.dateRange) {
        params.dateFrom = state.dateRange.dateFrom
        params.dateTo = state.dateRange.dateTo
      }

      return params
    }

    const getEvents = async(isDateRangeChanged = false, isFirstRequest = false): Promise<void> => {
      await Promise.all([getEventsData(isDateRangeChanged, isFirstRequest), getEventsDates()])
    }

    const getEventsDates = async() => {
      try {
        const response = await eventApi.getEventsDates(venueInfo.value.id)

        if (response?.data?.length) {
          state.eventsDates = response.data
        } else {
          state.eventsDates = []
        }
      } catch (e) {
        state.eventsDates = []
      }
    }

		const getEventsData = async (isDateRangeChanged = false, isFirstRequest = false): Promise<void> => {
			try {
        const params = getEventsParams(isDateRangeChanged, isFirstRequest)

				loading.value = true

				const response = await eventApi.getEvents(params)

				if (!response?.data?.data) return

        if (response?.data?.meta?.latestEventStartDate) {
          const latestEventDate = response?.data?.meta?.latestEventStartDate
          const today = moment().format('YYYY-MM-DD')
          const isLatestEventInPast = moment(
              latestEventDate,
              'YYYY-MM-DD'
          ).isBefore(today)

          latestEventStartDate.value = isLatestEventInPast ? today : response?.data?.meta?.latestEventStartDate

        }

				const formattedResponse = response.data.data.map((event: EventEntity) => ({
					...event,
					monthYear: `${getMonth(event.startDate)} ${getYear(event.startDate)}`,
					entryTime: getFormattedTime(event.entryTime, '', TIME_FORMATS.AMPM),
          eventImages: event.eventImages.sort((a, b) => a.position - b.position),
          period: {
            dateFrom: getFormattedDate(event.startDate, null, DATE_FORMATS.fullWithShortNames),
            timeFrom: getFormattedTime(event.startTime, null, TIME_FORMATS.AMPM)
          },
					eventStartDateInfo: getEventStartDateInfo(
						`${event.startDate} ${event.startTime}`,
						''
					),
				}))

				if (!formattedResponse.length) {
					isLastResponseEmpty.value = true

          if (currentPage == 1) {
            state.eventsList = []
            isEmptyResult.value = true
          }

					return
				} else if (formattedResponse.length && isEmptyResult.value) {
          isEmptyResult.value = false
        }

        if (isDateRangeChanged) {
          state.eventsList = formattedResponse

          return
        }

				state.eventsList.push(...formattedResponse)
			} catch (e) {
				console.log('error', e)
			} finally {
				loading.value = false
			}
		}

		return {
			events,
			state,
			loading,
			firstLoading,
			isLastResponseEmpty,
			eventsGroups,
			isLoadMoreVisible,
      latestEventStartDate,
      isEmptyResult,
      placeholderIcon,
      rangePickerFormat,
      isMobileScreen,
      unavailableEventModalOpen,
			isBannerVisible,
			minPickerDate,
			handleLoadMore,
      isNextYear,
      dateRangeChanged,
      closeUnavailableEventModal,
      closeFooter
		}
	}
})
